import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Coordinates,
  GetShopClusterListRequest,
  GetShopClusterListResponse,
  GetShopGunGusResponse,
  GetShopListRequest,
  GetShopListResponse,
  GunGu,
  ShippingAddress,
  SiDo,
} from '@remodeling/shop';
import querystring from 'query-string';
import _, { debounce } from 'lodash';
import useSWRInfinite from 'swr/infinite';
import {
  SOUTH_KOREA_LEVEL_MAX,
  SOUTH_KOREA_LAT,
  SOUTH_KOREA_LNG,
  SHOP_ITEM_TYPE,
  GUN_GU_LEVEL,
  SI_DO_LEVEL,
  SOUTH_KOREA_LEVEL_MIN,
  CURRENT_LEVEL,
  SHOP_REMODELING_FILTER,
} from '@/constants';
import useSWR, { KeyLoader } from 'swr';
import { shopSearchGateway } from '@/di-container/shop/search/gateway/client';
import { useRouter } from 'next/router';
import {
  GeoLocationRegistration,
  GetLocationData,
  MapSearchFilter,
  SearchIpResponse,
  WebAppMessage,
} from '@/types';
import { useWeb } from '@/providers/WebProvider/WebProvider';
import {
  selectShopSearchUrlParam,
  setShopSearchUrlParam,
} from '@/redux/reducers/shopSearch';
import { useSelector, useStore } from 'react-redux';
import useWindowHistory from '@/hooks/useWindowHistory';
import TrackingService from '@/services/tracking';
import { searchAddressIpGateway } from '@/di-container/ip/gateway/client';
import { shopGeoLocationGateway } from '@/di-container/shop/geo-location/client';
import { DropdownOptionItem } from '@design-system/pc';
export type BottomSheetLevel = 'TOP' | 'MIDDLE' | 'BOTTOM';

export const REMODELING_SHOP = '/shop/search';

interface UseMaps {
  sidoList?: SiDo[];
  address?: ShippingAddress;
  st: string;
}

const CALLBACK_ID_GET_LOCATION = 'getLocation';

const useMaps = ({ sidoList, address, st }: UseMaps) => {
  const router = useRouter();
  const store = useStore();
  const { handleGetLocation } = useWeb();
  const { handleReplaceState } = useWindowHistory();
  // const [init, setInit] = useState<number>(0);
  const [openState, setOpenState] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pages, setPages] = useState<number>(1);

  const urlParam = useSelector(selectShopSearchUrlParam);
  const [level, setLevel] = useState<number>(
    urlParam?.zoomLevel ?? SOUTH_KOREA_LEVEL_MAX,
  );
  const [center, setCenter] = useState<Coordinates>({
    latitude: urlParam?.latitude ?? SOUTH_KOREA_LAT,
    longitude: urlParam?.longitude ?? SOUTH_KOREA_LNG,
  });

  const mapRef = useRef<any>();

  const isApiCall = useRef<{
    postGeoLocation: boolean;
  }>({
    postGeoLocation: false,
  });

  const realMapRef = useMemo(
    () => (mapRef.current ? (mapRef.current as any) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapRef.current],
  );

  const [hoverItem, setHoverItem] = useState<number | null>(null);
  const [area, setArea] =
    useState<
      Pick<
        GetShopListRequest,
        | 'cenLatitude'
        | 'cenLongitude'
        | 'neLatitude'
        | 'neLongitude'
        | 'swLatitude'
        | 'swLongitude'
      >
    >();

  const [sido, setSido] = useState<SiDo | undefined>(undefined);
  const [gungu, setGungu] = useState<GunGu | undefined>(undefined);
  const [groupCode, setGroupCode] = useState<MapSearchFilter>({
    id: 0,
    text: '전체',
    code: 'ALL',
  });
  const [itemTypes, setItemTypes] = useState<MapSearchFilter[]>([]);
  const [isShowReSearchButton, setIsShowReSearchButton] =
    useState<boolean>(false);
  const [isDenied, setIsDenied] = useState<boolean | undefined>(undefined);

  const isCommonMainShop = useMemo(
    () => router.pathname === REMODELING_SHOP,
    [router.pathname],
  );

  /**
   * API
   */

  const [isAutoFitZoom, setAutoFitZoom] = useState<boolean>(false);
  // ========= 매장 목록 API START =========
  const getShopListKey: KeyLoader = useCallback(
    (pageIndex: number) => {
      if (level < 12) {
        return querystring.stringify({
          page: pages,
          size: 10,
          ...area,
          groupCode: isCommonMainShop ? undefined : groupCode.code,
          itemTypes: isCommonMainShop
            ? itemTypes.length > 0
              ? itemTypes.map((x) => x.code)
              : SHOP_ITEM_TYPE.map((x) => x.code)
            : undefined,
        });
      } else {
        return querystring.stringify({
          page: pages,
          size: 10,
          groupCode: isCommonMainShop ? undefined : groupCode.code,
          itemTypes: isCommonMainShop
            ? itemTypes.length > 0
              ? itemTypes.map((x) => x.code)
              : SHOP_ITEM_TYPE.map((x) => x.code)
            : undefined,
        });
      }
    },
    [area, groupCode.code, isCommonMainShop, itemTypes, level, pages],
  );

  /**
   * Uri 업데이트
   */
  const updateUri = useCallback(
    ({
      latitude,
      longitude,
      zoomLevel,
    }: {
      latitude?: number;
      longitude?: number;
      zoomLevel?: number;
    }) => {
      if (latitude && longitude && zoomLevel) {
        store.dispatch(
          setShopSearchUrlParam({
            latitude,
            longitude,
            zoomLevel,
          }),
        );

        handleReplaceState(
          null,
          '',
          TrackingService.retainQueryParam(`${router.pathname}`, {
            lat: latitude,
            lng: longitude,
            level: zoomLevel,
          }),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleReplaceState, store],
  );

  const { data: markerListApi } = useSWRInfinite<GetShopListResponse>(
    (pageIndex, previousPageData) => {
      if (previousPageData && previousPageData.data?.content.length === 0)
        return null;
      return getShopListKey(pageIndex, previousPageData);
    },
    (query) => {
      // setInit((prev) => prev + 1);
      setIsLoading(true);
      return shopSearchGateway().getShopListData.exec({
        ...(querystring.parse(query) as unknown as GetShopListRequest),
      });
    },
  );

  const markerListApiData = useMemo(() => {
    const response = markerListApi?.map((response) => response?.data?.content);
    if (markerListApi?.[0].code === 200) {
      setIsLoading(false);
      setOpenState(true);
      setIsShowReSearchButton(false);
    }
    return (
      response?.reduce((prev = [], accumulator = []) =>
        prev.concat(accumulator),
      ) || []
    );
  }, [markerListApi]);

  const shopListNumber = useMemo(() => {
    const response = markerListApi?.find((response) => response?.data)?.data;
    return {
      totalElements: response?.totalElements,
      totalPages: response?.totalPages,
    };
  }, [markerListApi]);

  // ========= 필터 관련 훅 START =========

  const handleClickItemTypes = useCallback(
    async (param: MapSearchFilter) => {
      if (itemTypes.find((x) => x === param) === undefined) {
        setItemTypes([...itemTypes, param]);
      } else {
        setItemTypes(itemTypes.filter((x) => x !== param));
      }
    },
    [itemTypes],
  );

  const handleClickItemTypesReset = useCallback(async () => {
    setItemTypes([]);
    setGroupCode({ code: 'ALL', id: 0 });
  }, []);

  const handleChangegroupCode = useCallback(
    async (selectedItem: MapSearchFilter) => {
      if (selectedItem.code === groupCode.code) {
        setGroupCode({ id: 0, text: '전체', code: 'ALL' });
      } else {
        setGroupCode(selectedItem);
      }
    },
    [groupCode],
  );
  // ========= 필터 관련 훅 END =========

  // ========= 매장 목록 API END =========

  // ========= 클러스터 API START =========
  const getClusterKey: KeyLoader = useCallback(
    (pageIndex: number) => {
      return level >= SOUTH_KOREA_LEVEL_MIN
        ? querystring.stringify({
            groupCode: isCommonMainShop ? undefined : groupCode.code,
            itemTypes: isCommonMainShop
              ? itemTypes.length > 0
                ? itemTypes.map((x) => x.code)
                : SHOP_ITEM_TYPE.map((x) => x.code)
              : undefined,
          })
        : null;
    },
    [groupCode, isCommonMainShop, itemTypes, level],
  );

  const { data: clusterList } = useSWRInfinite<GetShopClusterListResponse>(
    getClusterKey,
    (query) => {
      return shopSearchGateway().getShopClusterListData.exec({
        ...(querystring.parse(query) as unknown as GetShopClusterListRequest),
      });
    },
  );

  const clusterListApiData = useMemo(() => {
    if (clusterList?.[0].code !== 200) {
      return undefined;
    }
    return clusterList?.[0].data?.clusters;
  }, [clusterList]);

  // ========= 클러스터 API END =========

  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isSearchLoading) return;
    // if (isAutoFitZoom) return;

    changeShopList();
    setIsSearchLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchLoading]);

  // 시,도 변경 이벤트
  const handleChangeSido = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const {
        custom_field_1: name,
        custom_field_2: code,
        custom_field_3: latitude,
        custom_field_4: longitude,
      } = e.currentTarget.dataset;

      if (name && code && latitude && longitude) {
        setSido({
          name,
          code,
          latitude: Number(latitude),
          longitude: Number(longitude),
        });

        setGungu(undefined);

        // mapRef.current?.setCenter(
        //   new window.kakao.maps.LatLng(Number(latitude), Number(longitude)),
        // );

        // mapRef.current?.setLevel(SI_DO_LEVEL);

        updateUri({
          latitude: Number(latitude),
          longitude: Number(longitude),
          zoomLevel: SI_DO_LEVEL,
        });
      } else {
        setSido(undefined);
        setGungu(undefined);

        mapRef.current?.setCenter(
          new window.kakao.maps.LatLng(
            Number(SOUTH_KOREA_LAT),
            Number(SOUTH_KOREA_LNG),
          ),
        );

        mapRef.current?.setLevel(SOUTH_KOREA_LEVEL_MAX);
      }
    },
    [updateUri],
  );
  // 군,구 변경 이벤트

  const handleChangeGunGu = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const {
        custom_field_1: name,
        custom_field_2: code,
        custom_field_3: latitude,
        custom_field_4: longitude,
      } = e.currentTarget.dataset;

      if (name && code && latitude && longitude) {
        setGungu({
          name,
          code,
          latitude: Number(latitude),
          longitude: Number(longitude),
        });

        // mapRef.current?.setCenter(
        //   new window.kakao.maps.LatLng(Number(latitude), Number(longitude)),
        // );

        // mapRef.current?.setLevel(GUN_GU_LEVEL);

        updateUri({
          latitude: Number(latitude),
          longitude: Number(longitude),
          zoomLevel: GUN_GU_LEVEL,
        });
      } else {
        setGungu(undefined);

        if (sido) {
          mapRef.current?.setCenter(
            new window.kakao.maps.LatLng(
              Number(sido.latitude),
              Number(sido.longitude),
            ),
          );

          mapRef.current?.setLevel(SI_DO_LEVEL);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sido],
  );

  // ========= 시,도 API Format START =========
  const convertSidoDropdownOptionFormat = useMemo(() => {
    return (
      sidoList?.map((obj) => {
        return {
          text: obj.name,
          value: obj.code,
          customProps: {
            'data-custom_field_1': obj.name,
            'data-custom_field_2': obj.code,
            'data-custom_field_3': _.toString(obj.latitude),
            'data-custom_field_4': _.toString(obj.longitude),
          },
        } as unknown as DropdownOptionItem;
      }) || []
    );
  }, [sidoList]);
  // ========= 시,도 API Format END =========

  // ========= 군구 목록 API START =========

  const gunGuFetchear = async () => {
    const res = await shopSearchGateway().getGunGuData.exec({
      code: sido?.code as string,
    });

    if (res.code !== 200) {
      // console.log(res.code, res.message);
    }

    return res;
  };

  const { data: gunguListApi } = useSWR<GetShopGunGusResponse>(
    sido ? `/sido/${sido.code}` : null,
    gunGuFetchear,
  );

  const convertGunGuDropdownOptionFormat = useMemo(() => {
    return (
      gunguListApi?.data?.gunGus.map((obj) => {
        return {
          text: obj.name,
          value: obj.code,
          customProps: {
            'data-custom_field_1': obj.name,
            'data-custom_field_2': obj.code,
            'data-custom_field_3': _.toString(obj.latitude),
            'data-custom_field_4': _.toString(obj.longitude),
          },
        } as unknown as DropdownOptionItem;
      }) || []
    );
  }, [gunguListApi?.data?.gunGus]);

  // ========= 군구 목록 API END =========

  const changeShopList = useCallback(async () => {
    if (!realMapRef) return;

    const proj = realMapRef.getProjection();
    const bounds = realMapRef.getBounds();

    const sw = proj.pointFromCoords(bounds.getSouthWest());
    const ne = proj.pointFromCoords(bounds.getNorthEast());

    const mapBoundWithoutBottomSheet = new window.kakao.maps.LatLngBounds(
      proj.coordsFromPoint(sw),
      proj.coordsFromPoint(ne),
    );

    await setArea({
      cenLatitude: center.latitude,
      cenLongitude: center.longitude,
      neLatitude: mapBoundWithoutBottomSheet.getNorthEast().getLat(),
      neLongitude: mapBoundWithoutBottomSheet.getNorthEast().getLng(),
      swLatitude: mapBoundWithoutBottomSheet.getSouthWest().getLat(),
      swLongitude: mapBoundWithoutBottomSheet.getSouthWest().getLng(),
    });
  }, [center.latitude, center.longitude, realMapRef]);

  const handleClickReSearchButton = useCallback(async () => {
    changeShopList();
  }, [changeShopList]);

  const handleChangeCenter = useCallback(
    (map: any) => {
      setAutoFitZoom(false);

      setCenter({
        latitude: map.getCenter().getLat(),
        longitude: map.getCenter().getLng(),
      });
      updateUri({
        latitude: map.getCenter().getLat(),
        longitude: map.getCenter().getLng(),
        zoomLevel: map.getLevel(),
      });

      if (!isAutoFitZoom) {
        setIsShowReSearchButton(true);
      }
    },
    [isAutoFitZoom, updateUri],
  );

  /**
   * 주어진 영역이 화면 안에 검색된 매장이 전부 나타날 수 있도록 지도의 중심 좌표와 확대 수준을 설정
   */
  // const handleFitMarkersToBounds = useCallback(async () => {
  //   // 시도, 군구 코드정보만으로 검색한 매장 리스트 조회
  //   const response = await shopSearchGateway().getShopListData.exec({
  //     page: pages || 1,
  //     size: 10,
  //     sidoCd: sido?.code,
  //     sggCd: gungu?.code,
  //     itemTypes: [],
  //     groupCode: 'ALL',
  //   });

  //   // 각 매장 리스트의 위도, 경도 정보를 기준으로 지도의 중심점을 설정
  //   const points = response.data?.content.map(
  //     (shop) =>
  //       new window.kakao.maps.LatLng(
  //         shop.address.coordinates.latitude,
  //         shop.address.coordinates.longitude,
  //       ),
  //   );
  //   const bounds = new window.kakao.maps.LatLngBounds();
  //   points?.forEach((point) => {
  //     bounds.extend(point);
  //   });

  //   // 지도에 설정된 중심점을 변경
  //   mapRef.current?.setBounds(bounds);

  //   changeShopList();
  // }, [changeShopList, gungu?.code, pages, sido?.code]);

  /**
   * 지도검색 버튼 클릭 이벤트
   */
  const handleClickMapSearchButton = useCallback(async () => {
    setIsSearchLoading(true);
    setAutoFitZoom(true);

    if (sido && sido.code === '50') {
      // handleFitMarkersToBounds();
      mapRef.current?.setLevel(SI_DO_LEVEL);
      setCenter({
        latitude: sido.latitude! - 0.2,
        longitude: sido.longitude!,
      });
      return;
    }

    if (sido && !gungu) {
      mapRef.current?.setLevel(SI_DO_LEVEL);
      setCenter({
        latitude: sido.latitude!,
        longitude: sido.longitude!,
      });
    }

    if (sido && gungu) {
      mapRef.current?.setLevel(GUN_GU_LEVEL);
      setCenter({
        latitude: gungu.latitude!,
        longitude: gungu.longitude!,
      });
    }
  }, [gungu, sido]);

  const handleChangePages = useCallback((nextPage: number) => {
    setPages(nextPage);
  }, []);

  const handleChangeDebounceCenter = debounce((map: any) => {
    handleChangeCenter(map);
  }, 200);

  const handleClickCluster = useCallback((coordinates: Coordinates) => {
    mapRef.current?.setCenter(
      new window.kakao.maps.LatLng(coordinates.latitude, coordinates.longitude),
    );
    mapRef.current?.setLevel(SI_DO_LEVEL);
  }, []);

  const showCurrentMarker = useCallback(
    (latitude: number, longitude: number) => {
      mapRef.current?.setCenter(
        new window.kakao.maps.LatLng(latitude, longitude),
      );
      mapRef.current?.setLevel(CURRENT_LEVEL);
    },
    [],
  );

  const getLocation = useCallback(
    async (message: WebAppMessage<GetLocationData>) => {
      if (message.code === 200 && message.data) {
        if (!isApiCall.current.postGeoLocation) {
          isApiCall.current.postGeoLocation = true;

          searchAddressIpGateway()
            .getSearchIp.exec()
            .then((res: SearchIpResponse) => {
              const result = res;
              if (result.code === 200) {
                const req: GeoLocationRegistration = {
                  ip: result.data?.ivIp || '',
                  pageAreaCode: isCommonMainShop ? 'MAIN' : 'RH',
                };

                shopGeoLocationGateway().postGeoLocation.exec(req);
              }
              isApiCall.current.postGeoLocation = false;
            })
            .catch(() => {
              isApiCall.current.postGeoLocation = false;
            });
        }

        const latitude = message.data.latitude;
        const longitude = message.data.longitude;
        showCurrentMarker(latitude, longitude);
        setIsDenied(false);
      } else {
        if (isDenied === undefined) {
          setIsDenied(true);
        }
      }
    },
    [isCommonMainShop, isDenied, showCurrentMarker],
  );

  const handleClickCurrentLoactionButton = useCallback(() => {
    handleGetLocation(CALLBACK_ID_GET_LOCATION, getLocation);
  }, [getLocation, handleGetLocation]);

  const handleToggleMenu = useCallback(() => {
    setOpenState((pre) => !pre);
    setTimeout(() => {
      realMapRef.relayout();
    }, 250);
  }, [realMapRef]);

  // 페이지네이션 초기화
  useEffect(() => {
    setPages(1);
  }, [area, sido, gungu, groupCode, itemTypes]);

  useEffect(() => {
    const findShopFilter = _.find(SHOP_REMODELING_FILTER, {
      code: st.toLocaleUpperCase() as
        | 'RM'
        | 'KB'
        | 'BR'
        | 'FN'
        | 'HG'
        | 'HC'
        | 'RH'
        | 'KC'
        | 'CF'
        | 'IF'
        | 'BS'
        | 'ALL'
        | 'OH',
    });

    if (findShopFilter) {
      setGroupCode(findShopFilter);
    }
  }, [st]);

  useEffect(() => {
    handleGetLocation(null, getLocation);
  }, [getLocation, handleGetLocation]);

  return {
    center,
    openState,
    level,
    mapRef,
    isShowReSearchButton,
    markerListApiData,
    hoverItem,
    shopListNumber,
    sido,
    gungu,
    setSido,
    setGungu,
    groupCode,
    itemTypes,
    pages,
    isLoading,
    convertSidoDropdownOptionFormat,
    convertGunGuDropdownOptionFormat,
    clusterListApiData,
    setOpenState,
    handleClickItemTypes,
    handleClickItemTypesReset,
    handleClickCurrentLoactionButton,
    handleChangegroupCode,
    handleClickCluster,
    setHoverItem,
    setLevel,
    setPages,
    handleChangeDebounceCenter,
    handleClickReSearchButton,
    handleChangeSido,
    handleChangeGunGu,
    handleClickMapSearchButton,
    handleToggleMenu,
    handleChangePages,
  };
};

export default useMaps;
